import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api';
import { formatMoney } from '../marketing/model';

interface BillingAccount {
  id: string;
  companyId: string;
  name?: string;
  balance: string;
  buffer?: string;
  spendable?: string;
  reserved?: string;
}

export const BILLING_KEY = ['marketing', 'billing'] as const;

/** Баланс для шапки и чек-листа. GET /api/billing (тот же ключ, что у BillingPage), раз в минуту.
 * Один счёт → label = formatMoney(balance); несколько → null (сумму по строкам не считаем). */
export function useBalanceSummary(enabled: boolean): {
  status: 'idle' | 'pending' | 'error' | 'ready';
  label: string | null;
  accounts: { id: string; companyId: string; balance: string; spendable: string; buffer: string | null }[];
} {
  const query = useQuery({
    queryKey: BILLING_KEY,
    queryFn: ({ signal }) =>
      apiGet<{ accounts?: BillingAccount[]; account?: BillingAccount }>('/api/billing', signal),
    enabled,
    refetchInterval: 60_000,
  });
  if (!enabled) return { status: 'idle', label: null, accounts: [] };
  if (query.isPending) return { status: 'pending', label: null, accounts: [] };
  if (query.isError) return { status: 'error', label: null, accounts: [] };
  const raw = query.data.accounts ?? (query.data.account ? [query.data.account] : []);
  const accounts = raw.map((item) => ({
    id: item.id,
    companyId: item.companyId,
    balance: item.balance,
    spendable: item.spendable ?? item.balance,
    // Запас приходит из настроек сети — своего значения не придумываем.
    buffer: item.buffer ?? null,
  }));
  return {
    status: 'ready',
    label: accounts.length === 1 ? formatMoney(accounts[0].balance) : null,
    accounts,
  };
}
